/**
 * Export Lexical EditorState to Telegram MarkdownV2 format.
 *
 * MarkdownV2 has extensive escaping requirements and special handling
 * for the italic + underline combination.
 */
import type { EditorState } from 'lexical';
import {
  walkTree,
  isInCodeBlock,
  type TextSegment,
  type ElementContext,
  type InlineFormat,
} from './tree-walker';

/**
 * Characters that must be escaped outside any entity.
 * Note: We escape backslash first to avoid double-escaping.
 * The hyphen is placed at the end of the character class to avoid range issues.
 */
const MARKDOWN_ESCAPE_CHARS = /[_*[\]()~`>#+=|{}.!\\-]/g;

/**
 * Characters that must be escaped inside code/pre.
 */
const CODE_ESCAPE_CHARS = /[`\\]/g;

/**
 * Characters that must be escaped inside link URL parentheses.
 */
const URL_ESCAPE_CHARS = /[)\\]/g;

/**
 * Escapes text for MarkdownV2 (outside entities).
 */
function escapeMarkdownV2(text: string): string {
  return text.replace(MARKDOWN_ESCAPE_CHARS, '\\$&');
}

/**
 * Escapes text inside code/pre blocks.
 */
function escapeCodeContent(text: string): string {
  return text.replace(CODE_ESCAPE_CHARS, '\\$&');
}

/**
 * Escapes URL for use in markdown link syntax.
 */
function escapeLinkUrl(url: string): string {
  return url.replace(URL_ESCAPE_CHARS, '\\$&');
}

/**
 * Formats a segment with markdown markers based on active formats.
 */
function formatSegmentWithMarkers(
  text: string,
  formats: Set<InlineFormat>,
  isCodeBlock: boolean
): string {
  // Inside code blocks, only escape backtick and backslash
  if (isCodeBlock) {
    return escapeCodeContent(text);
  }

  // Handle inline code specially - it takes precedence
  if (formats.has('code')) {
    return '`' + escapeCodeContent(text) + '`';
  }

  // Escape the text first
  let result = escapeMarkdownV2(text);

  // Apply markers from innermost to outermost
  if (formats.has('strikethrough')) {
    result = '~' + result + '~';
  }

  const hasItalic = formats.has('italic');
  const hasUnderline = formats.has('underline');

  if (hasItalic && hasUnderline) {
    // Special case: use ___text_\r__ pattern to disambiguate
    result = '___' + result + '_\r__';
  } else if (hasUnderline) {
    result = '__' + result + '__';
  } else if (hasItalic) {
    result = '_' + result + '_';
  }

  if (formats.has('bold')) {
    result = '*' + result + '*';
  }

  return result;
}

/**
 * State for tracking open element contexts in markdown output.
 */
interface MarkdownState {
  inSpoiler: boolean;
  inBlockquote: boolean;
  inExpandableBlockquote: boolean;
  inCodeBlock: boolean;
  codeBlockLang?: string;
  inLink: boolean;
  linkUrl?: string;
  linkUserId?: number;
  pendingLinkText: string;
}

/**
 * Creates initial markdown state.
 */
function createInitialState(): MarkdownState {
  return {
    inSpoiler: false,
    inBlockquote: false,
    inExpandableBlockquote: false,
    inCodeBlock: false,
    inLink: false,
    pendingLinkText: '',
  };
}

/**
 * Prefixes each line with the blockquote marker.
 */
function prefixBlockquoteLines(
  text: string,
  expandable: boolean
): string {
  const prefix = expandable ? '**>' : '>';
  return text
    .split('\n')
    .map((line) => prefix + line)
    .join('\n');
}

/**
 * Builds MarkdownV2 from segments, handling element context transitions.
 */
function buildMarkdownV2(segments: TextSegment[]): string {
  const parts: string[] = [];
  const state = createInitialState();
  let previousContext: ElementContext[] = [];

  // Track blockquote content separately to prefix lines
  let blockquoteContent: string[] = [];
  let blockquoteExpandable = false;

  for (const segment of segments) {
    const currentContext = segment.elementContext;
    const segmentInCodeBlock = isInCodeBlock(segment);

    // Find closed and opened contexts
    const closedContexts = previousContext.filter(
      (prev) => !currentContext.some((curr) => curr.key === prev.key)
    );
    const openedContexts = currentContext.filter(
      (curr) => !previousContext.some((prev) => prev.key === curr.key)
    );

    // Handle closed contexts
    for (const ctx of closedContexts) {
      switch (ctx.type) {
        case 'spoiler':
          parts.push('||');
          state.inSpoiler = false;
          break;
        case 'blockquote':
        case 'expandable_blockquote':
          // Flush blockquote content with prefixes
          if (blockquoteContent.length > 0) {
            const content = blockquoteContent.join('');
            parts.push(prefixBlockquoteLines(content, blockquoteExpandable));
            blockquoteContent = [];
          }
          state.inBlockquote = false;
          state.inExpandableBlockquote = false;
          break;
        case 'code_block':
          parts.push('\n```');
          state.inCodeBlock = false;
          state.codeBlockLang = undefined;
          break;
        case 'link':
          // Emit the complete link
          if (state.linkUserId !== undefined) {
            parts.push(
              `[${state.pendingLinkText}](tg://user?id=${String(state.linkUserId)})`
            );
          } else if (state.linkUrl) {
            parts.push(
              `[${state.pendingLinkText}](${escapeLinkUrl(state.linkUrl)})`
            );
          }
          state.inLink = false;
          state.linkUrl = undefined;
          state.linkUserId = undefined;
          state.pendingLinkText = '';
          break;
        case 'custom_emoji':
          // Custom emoji in MarkdownV2 is just the fallback text
          // (Telegram doesn't have a markdown syntax for custom emoji)
          break;
      }
    }

    // Handle opened contexts
    for (const ctx of openedContexts) {
      switch (ctx.type) {
        case 'spoiler':
          parts.push('||');
          state.inSpoiler = true;
          break;
        case 'blockquote':
          state.inBlockquote = true;
          blockquoteExpandable = false;
          break;
        case 'expandable_blockquote':
          state.inExpandableBlockquote = true;
          blockquoteExpandable = true;
          break;
        case 'code_block':
          state.inCodeBlock = true;
          state.codeBlockLang = ctx.language;
          parts.push('```' + (ctx.language ?? '') + '\n');
          break;
        case 'link':
          state.inLink = true;
          state.linkUrl = ctx.url;
          state.linkUserId = ctx.userId;
          state.pendingLinkText = '';
          break;
        case 'custom_emoji':
          // Just render the fallback text
          break;
      }
    }

    previousContext = currentContext;

    // Render the segment content
    const formattedText = formatSegmentWithMarkers(
      segment.text,
      segment.formats,
      segmentInCodeBlock
    );

    // Route the content appropriately
    if (state.inLink) {
      // Accumulate link text (already formatted)
      state.pendingLinkText += formattedText;
    } else if (state.inBlockquote || state.inExpandableBlockquote) {
      // Accumulate blockquote content
      blockquoteContent.push(formattedText);
    } else {
      parts.push(formattedText);
    }
  }

  // Close any remaining open contexts
  for (let i = previousContext.length - 1; i >= 0; i--) {
    const ctx = previousContext[i];
    if (!ctx) continue;
    switch (ctx.type) {
      case 'spoiler':
        parts.push('||');
        break;
      case 'blockquote':
      case 'expandable_blockquote':
        if (blockquoteContent.length > 0) {
          const content = blockquoteContent.join('');
          parts.push(
            prefixBlockquoteLines(
              content,
              ctx.type === 'expandable_blockquote'
            )
          );
          blockquoteContent = [];
        }
        break;
      case 'code_block':
        parts.push('\n```');
        break;
      case 'link':
        if (state.linkUserId !== undefined) {
          parts.push(
            `[${state.pendingLinkText}](tg://user?id=${String(state.linkUserId)})`
          );
        } else if (state.linkUrl) {
          parts.push(
            `[${state.pendingLinkText}](${escapeLinkUrl(state.linkUrl)})`
          );
        }
        break;
    }
  }

  return parts.join('');
}

/**
 * Convert a Lexical EditorState to a Telegram MarkdownV2 string.
 * All special characters are escaped according to Telegram's rules.
 *
 * @param editorState - The Lexical editor state to export.
 * @returns A MarkdownV2-formatted string ready for Telegram's parser.
 */
export function exportToMarkdownV2(editorState: EditorState): string {
  const segments = walkTree(editorState);
  const markdown = buildMarkdownV2(segments);

  // Trim trailing newlines
  return markdown.replace(/\n+$/, '');
}
